// Live payroll client — maps the gateway/payroll-service responses onto the UI
// model. The backend is operations-first and leaner than the UI types, so this
// layer enriches runs with legal-entity country/name and classifies pay
// components client-side. Selected by payrollApi.ts unless VITE_PAYROLL_MOCK=true.

import axios from 'axios';
import { toDisplayDate } from '@/lib/orgFormat';
import { apiConfig } from '@/config/api.config';
import type {
  PayrollRun,
  CreateRunResult,
  RunDetail,
  RunState,
  EmployeeLine,
  Approval,
  ApprovalLevel,
  ApprovalDecision,
  PayrollExport,
  ExportType,
  ExportFormat,
  ExportStatus,
  DisbursementBatch,
  DisbursementBatchStatus,
  DisbursementPurpose,
  DisbursementItem,
  DisbursementItemStatus,
  DisbursementChannel,
  RetryClass,
  Payslip,
  PayComponent,
  ComponentKind,
  CountryCode,
  Currency,
  LegalEntityLite,
  EmployeeLite,
  ControlCenter,
  RiskAlert,
  AlertSeverity,
  AlertCategory,
  AlertStatus,
  GateStatus,
  Filing,
  FilingRegister,
  FilingAmount,
  FilingAmountStatus,
  FilingType,
  FilingStatus,
  FilingEvidence,
  FilingOutcomeInput,
  NewFilingInput,
  OutstandingFiling,
  SubmissionDestination,
  RunExternalPaymentMethod,
} from '../types';
import type { PayrollApiContract } from './contract';
import { toLineStatus, countRunLines } from '../lib/runLines';
import { getApiErrorMessage } from '@/lib/apiError';
import { attachCookieAuth } from '@/lib/session';

const EMPLOYEES_UNAVAILABLE = 'The employee list for this run could not be loaded.';
const EMPLOYEES_INCOMPLETE = 'The employee list for this run came back incomplete.';

const API = '/api/v1';
const num = (s: string | number | undefined): number => (s == null ? 0 : Number(s));

// Dedicated gateway client. Auth is the httpOnly session cookie (sent via
// withCredentials); attachCookieAuth also echoes the CSRF token on writes. We do
// NOT nuke the session on error, so a payroll failure degrades to an empty state.
const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

async function get<T = any>(url: string): Promise<T> {
  return (await http.get<T>(url)).data;
}
async function post<T = any>(url: string, body?: any): Promise<T> {
  return (await http.post<T>(url, body ?? {})).data;
}
async function put<T = any>(url: string, body?: any): Promise<T> {
  return (await http.put<T>(url, body ?? {})).data;
}

// --- legal-entity enrichment (run list/header lack country + name) --------

interface LegalEntity {
  id: string;
  name: string;
  country: CountryCode;
  base_currency?: Currency;
}
let leCache: Map<string, LegalEntity> | null = null;

// legalEntities loads the tenant's entities, cached for the run/filing name lookups.
// IMPORTANT: never cache an EMPTY result (a transient failure, or a load before the
// admin created any) — doing so used to poison the cache so the "New run" picker
// showed "No legal entities yet" forever even though entities existed. We only cache
// a non-empty success; failures fall back to any prior cache, else an empty map that
// is NOT persisted (so the next call retries). Pass force to bypass the cache — the
// picker does, so it always reflects newly-created entities without a page reload.
async function legalEntities(force = false): Promise<Map<string, LegalEntity>> {
  if (!force && leCache && leCache.size > 0) return leCache;
  try {
    const data = await get<{ legal_entities: LegalEntity[] }>(`${API}/legal-entities`);
    const m = new Map((data.legal_entities ?? []).map((le) => [le.id, le]));
    if (m.size > 0) leCache = m; // only cache a real, non-empty result
    return m;
  } catch {
    return leCache ?? new Map(); // never poison the cache on failure
  }
}

// --- component classification (backend stores code+amount only) -----------

// The authoritative source of a line's kind is the engine's ComponentType,
// now sent by the backend as `type`. Classify by it whenever present so lines
// with no recognizable code prefix (benefit plans, allowances, benefit-in-kind)
// land in the RIGHT payslip section instead of defaulting to EARNINGS.
function kindFromType(type: string): ComponentKind | null {
  switch (type.toUpperCase()) {
    case 'EARNING':
    case 'NET_ADDITION':
    case 'BENEFIT_IN_KIND':
      return 'earning';
    case 'PRE_TAX_DEDUCTION':
      return 'pre_tax';
    case 'STATUTORY_TAX':
      return 'tax';
    case 'POST_TAX_DEDUCTION':
      return 'post_tax';
    case 'EMPLOYER_CONTRIBUTION':
      return 'employer';
    default:
      return null;
  }
}

// Fallback classifier for older runs whose read model doesn't carry the kind.
function classifyKind(code: string): ComponentKind {
  const c = code.toUpperCase();
  if (c.endsWith('_ER') || c === 'SDL' || c === 'WCF') return 'employer';
  if (c === 'PAYE' || c === 'PERSONAL_RELIEF') return 'tax';
  // Loan / advance / credit-service repayments are post-tax deductions.
  if (c.startsWith('LOAN:') || c.startsWith('ADVANCE:') || c.startsWith('CREDIT:')) return 'post_tax';
  if (c === 'SHIF' || c === 'HOUSING_EE' || c.startsWith('NHIF')) return 'post_tax';
  if (c.endsWith('_EE') || c.includes('PENSION') || c.includes('MATERNITY') || c === 'NSSF') return 'pre_tax';
  return 'earning';
}

const LABELS: Record<string, string> = {
  BASIC: 'Basic salary',
  PAYE: 'PAYE',
  PERSONAL_RELIEF: 'Personal relief',
  NSSF_EE: 'NSSF (employee)',
  NSSF_ER: 'NSSF (employer)',
  SHIF: 'SHIF',
  HOUSING_EE: 'Housing levy',
  HOUSING_ER: 'Housing levy (employer)',
  SDL: 'Skills Dev. Levy',
  WCF: "Workers' Comp.",
  RSSB_PENSION_EE: 'RSSB pension (employee)',
  RSSB_PENSION_ER: 'RSSB pension (employer)',
  RSSB_MATERNITY_EE: 'RSSB maternity (employee)',
  RSSB_MATERNITY_ER: 'RSSB maternity (employer)',
};
// Benefit lines carry a code with an id/type suffix (BENEFIT_PLAN:<uuid>,
// BENEFIT_ALLOWANCE:<type>, BENEFIT_DEDUCTION:<type>, BIK:<type>). They are
// employer/employee benefits, never statutory.
const isBenefit = (code: string) => /^(BENEFIT_PLAN|BENEFIT_ALLOWANCE|BENEFIT_DEDUCTION|BIK):/i.test(code);

const humanize = (code: string) => {
  // Loan/advance/credit repayment codes carry an id suffix (e.g. CREDIT:<uuid>);
  // show a friendly label, not the raw code.
  if (/^ADVANCE:/i.test(code)) return 'Advance repayment';
  if (/^(LOAN|CREDIT):/i.test(code)) return 'Loan repayment';
  // Benefit codes: strip the id/type suffix rather than print a raw UUID. The
  // plan NAME (when the backend sends one) is preferred in toComponent below.
  if (/^BENEFIT_PLAN:/i.test(code)) return 'Benefit plan';
  const benefitType = /^(BENEFIT_ALLOWANCE|BENEFIT_DEDUCTION|BIK):(.+)$/i.exec(code);
  if (benefitType) {
    const pretty = benefitType[2].replace(/[_-]/g, ' ').toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
    return /^BIK:/i.test(code) ? `${pretty} (benefit in kind)` : pretty;
  }
  return LABELS[code.toUpperCase()] ?? code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
};

function toComponent(code: string, rawAmount: number, name?: string, type?: string): PayComponent {
  // Prefer the engine ComponentType; fall back to code heuristics for old runs.
  const kind = (type && kindFromType(type)) || classifyKind(code);
  const mag = Math.abs(rawAmount);
  const credit = code.toUpperCase() === 'PERSONAL_RELIEF';
  const amount = kind === 'earning' || kind === 'employer' || credit ? mag : -mag;
  // Loan/advance/credit repayments and benefits are deductions/contributions but NOT statutory.
  const isRepayment = /^(LOAN|CREDIT|ADVANCE):/i.test(code);
  // Prefer the engine's line name (distinguishes "Advance repayment" vs "Loan
  // repayment", and carries the benefit PLAN name); fall back to a friendly
  // code-derived label when the backend didn't send one (never a raw UUID).
  const label = (name && name.trim()) || humanize(code);
  return { code, label, amount, kind, statutory: kind !== 'earning' && !isRepayment && !isBenefit(code) };
}

function periodLabel(periodStart: string): string {
  // period_start is a CIVIL date ("2026-05-01"), not an instant. new Date() parses
  // that as UTC midnight and toLocaleDateString renders it in the viewer's zone,
  // so anyone west of UTC saw the PREVIOUS month: a May run read "April 2026"
  // while the period line beside it correctly said May 1 – May 31.
  const d = toDisplayDate(periodStart);
  if (isNaN(d.getTime())) return periodStart;
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// --- run mapping ----------------------------------------------------------

interface RunView {
  id: string;
  legal_entity_id: string;
  frequency: string;
  period_start: string;
  period_end: string;
  state: RunState;
  currency: Currency;
  total_gross: string;
  total_net: string;
  total_employer_cost: string;
  employee_count?: number; // persisted per-run headcount (US-11); absent on detail
  label?: string;
  cancelled_at?: string;
  cancel_reason?: string;
  cancel_note?: string;
  paid_externally_at?: string;
  external_payment_method?: RunExternalPaymentMethod;
  external_payment_reference?: string;
  external_payment_note?: string;
  // Phase 5b: the approval policy the run was submitted under (absent before submission).
  policy_id?: string;
  policy_version?: number;
  policy_source?: 'builtin' | 'tenant' | 'entity';
}

function mapRun(r: RunView, le: Map<string, LegalEntity>, employeeCount?: number): PayrollRun {
  const ent = le.get(r.legal_entity_id);
  // Prefer an explicit count (detail path); else the list read-model's headcount.
  const count = employeeCount ?? r.employee_count ?? 0;
  const gross = num(r.total_gross);
  const net = num(r.total_net);
  const employerCost = num(r.total_employer_cost);
  return {
    id: r.id,
    legalEntityId: r.legal_entity_id,
    legalEntityName: ent?.name ?? '—',
    country: ent?.country ?? 'TZ',
    currency: r.currency,
    frequency: (r.frequency as PayrollRun['frequency']) || 'monthly',
    periodStart: r.period_start,
    periodEnd: r.period_end,
    periodLabel: periodLabel(r.period_start),
    state: r.state,
    // Blank and absent both mean "unnamed", so they collapse to undefined here
    // and the UI has one case to render rather than two.
    label: r.label?.trim() || undefined,
    cancelledAt: r.cancelled_at,
    cancelReason: r.cancel_reason || undefined,
    cancelNote: r.cancel_note || undefined,
    // Present only for a run settled outside KaziPeople; an in-platform payment
    // leaves all four absent.
    paidExternallyAt: r.paid_externally_at,
    externalPaymentMethod: r.external_payment_method || undefined,
    externalPaymentReference: r.external_payment_reference || undefined,
    externalPaymentNote: r.external_payment_note || undefined,
    policyId: r.policy_id || undefined,
    policyVersion: r.policy_version || undefined,
    policySource: r.policy_source || undefined,
    totals: {
      gross,
      net,
      employerCost,
      taxableIncome: 0,
      employeeDeductions: Math.max(0, gross - net),
      employerContributions: Math.max(0, employerCost - gross),
    },
    employeeCount: count,
    calculated: count,
    errored: 0,
    createdAt: r.period_start,
  };
}

const APPROVAL_LEVELS: ApprovalLevel[] = ['hr', 'finance', 'final'];

/**
 * The run's approval chain, in the order the SERVER enforces it. Phase 4: the chain comes
 * from the run's policy snapshot and may include a Group Finance stage (`scope: 'group'`) or
 * skip thresholded levels — re-deriving it from a fixed HR/Finance/Final list hid the group
 * stage and showed levels the run doesn't need. Only when the server sent nothing (the read
 * failed) do we fall back to the canonical three, all pending.
 */
export function mapApprovals(raw: any[] | undefined | null): Approval[] {
  const rows = Array.isArray(raw) ? raw.filter((x) => x && typeof x.level === 'string') : [];
  if (rows.length === 0) {
    return APPROVAL_LEVELS.map((level) => ({ level, decision: 'pending' as ApprovalDecision }));
  }
  return rows.map((a) => ({
    level: a.level as ApprovalLevel,
    decision: ((a.decision as ApprovalDecision) ?? 'pending') as ApprovalDecision,
    label: a.label || undefined,
    scope: a.scope === 'group' || a.level === 'group' ? ('group' as const) : undefined,
    note: a.note || undefined,
    decidedAt: a.decided_at || undefined,
  }));
}

/**
 * GET /payroll-runs/{id}/employees → EmployeeLine[]. Every line of the run:
 * calculated ones with their figures, then errored / excluded / not-yet-
 * calculated ones with their reason and NO figures — the zeros are
 * placeholders that are never rendered (see lib/runLines hasPay), and their
 * components are empty, so nothing can add them up as pay.
 */
export function mapRunEmployees(raw: any[] | undefined | null): EmployeeLine[] {
  return (Array.isArray(raw) ? raw : []).map((e) => {
    const status = toLineStatus(e.status);
    const paid = status === 'calculated';
    return {
      employeeId: e.employee_id,
      fullName: e.full_name,
      department: e.department || undefined,
      departmentId: e.department_id || undefined,
      status,
      errorReason: paid ? undefined : e.error_reason || undefined,
      gross: paid ? num(e.gross) : 0,
      taxableIncome: paid ? num(e.taxable_income) : 0,
      employeeDeductions: paid ? num(e.employee_deductions) : 0,
      employerContributions: paid ? num(e.employer_contributions) : 0,
      net: paid ? num(e.net) : 0,
      components: paid ? (e.lines ?? []).map((l: any) => toComponent(l.code, num(l.amount), l.name, l.type)) : [],
    };
  });
}

export const payrollApiLive: PayrollApiContract = {
  async listRuns(): Promise<PayrollRun[]> {
    const [data, le] = await Promise.all([
      get<{ runs: RunView[] }>(`${API}/payroll-runs`),
      legalEntities(),
    ]);
    return (data.runs ?? [])
      .map((r) => mapRun(r, le))
      .sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  },

  async getRunDetail(runId: string): Promise<RunDetail | null> {
    const le = await legalEntities();
    const run = await get<RunView>(`${API}/payroll-runs/${runId}`);

    const [empData, apData, expData, batches] = await Promise.all([
      // A failed Employees read is NOT an empty run (DEF-0mvnq1snl001-4c48d4):
      // keep the backend's message so the page can say so and offer a retry,
      // instead of "No employees in this run yet" and a headcount of 0.
      get<{ employees: any[] }>(`${API}/payroll-runs/${runId}/employees`)
        .then((d) => (Array.isArray(d?.employees)
          ? { employees: d.employees, error: undefined as string | undefined }
          : { employees: [], error: EMPLOYEES_INCOMPLETE }))
        .catch((e) => ({ employees: [] as any[], error: getApiErrorMessage(e, EMPLOYEES_UNAVAILABLE) })),
      get<{ approvals: any[] }>(`${API}/payroll-runs/${runId}/approvals`).catch(() => ({ approvals: [] })),
      get<{ exports: any[] }>(`${API}/payroll-runs/${runId}/exports`).catch(() => ({ exports: [] })),
      get<{ batches: any[] }>(`${API}/payroll-runs/${runId}/disbursements`).catch(() => ({ batches: [] })),
    ]);

    const employees = mapRunEmployees(empData.employees);
    const lineCounts = countRunLines(employees);

    const approvals = mapApprovals(apData.approvals);

    const exports: PayrollExport[] = (expData.exports ?? []).map(mapExport);
    const batch = (batches.batches ?? [])[0]
      ? await loadBatch((batches.batches ?? [])[0].id, run.currency)
      : null;

    if (empData.error) {
      // Unknown, not zero: no headcount is derived from a list we do not have.
      // The page reads employeesError and shows "—" + an error with Retry.
      const mapped = mapRun(run, le, 0);
      return { run: mapped, employees: [], employeesError: empData.error, approvals, exports, batch };
    }
    // Headcount stays the CALCULATED lines (what the run pays); the errored and
    // excluded ones only raise the card's warning hint.
    const mapped = mapRun(run, le, lineCounts.calculated);
    mapped.errored = lineCounts.error + lineCounts.excluded;
    return { run: mapped, employees, approvals, exports, batch };
  },

  async validateRun(runId) {
    // Large runs return 202 {async:true, job_id, total}; small runs return 200 and
    // the caller refreshes the detail. (C-PERF-1 Phase 2.)
    const res = await post<any>(`${API}/payroll-runs/${runId}/validate`);
    if (res && res.async) {
      return { async: true, jobId: String(res.job_id), total: Number(res.total ?? 0) };
    }
    return { async: false };
  },

  async getCalcStatus(runId) {
    try {
      const s = await get<any>(`${API}/payroll-runs/${runId}/calc-status`);
      return {
        status: s.status,
        processed: Number(s.processed ?? 0),
        total: Number(s.total ?? 0),
        error: s.error || undefined,
      };
    } catch {
      return null; // no job yet / not found
    }
  },

  async approveRun(runId, _level, _actor) {
    await post(`${API}/payroll-runs/${runId}/approve`);
    return this.getRunDetail(runId);
  },

  async cancelRun(runId, body) {
    await post(`${API}/payroll-runs/${runId}/cancel`, body);
  },

  async discardRun(runId) {
    await post(`${API}/payroll-runs/${runId}/discard`);
  },

  async setRunLabel(runId, label) {
    const res = await put<{ label?: string }>(`${API}/payroll-runs/${runId}/label`, { label });
    return res?.label ?? label.trim();
  },

  async rejectRun(runId, note) {
    await post(`${API}/payroll-runs/${runId}/reject`, { note });
    return this.getRunDetail(runId);
  },

  async revertRun(runId, note) {
    // Take an APPROVED (unlocked) run back to draft to correct + re-run. Backend clears
    // approvals; nothing to unwind (loans/exports only happen at lock).
    await post(`${API}/payroll-runs/${runId}/revert`, { note });
    return this.getRunDetail(runId);
  },

  async reopenRun(runId, note) {
    // Take a LOCKED run back to draft to re-calculate (e.g. after approving payout
    // methods). Backend clears approvals + refuses if a disbursement is in flight.
    await post(`${API}/payroll-runs/${runId}/reopen`, { note });
    return this.getRunDetail(runId);
  },

  async markRunPaid(runId, body) {
    // No optimistic state change here: the caller refetches, because reaching
    // 'paid' also makes six payslips visible and the page must show what the
    // server actually recorded, not what we asked for.
    await post(`${API}/payroll-runs/${runId}/mark-paid`, {
      method: body.method,
      reference: body.reference ?? '',
      note: body.note ?? '',
    });
  },

  async lockRun(runId, opts) {
    // Plain lock by default; after a 409 `risk_blocked` the caller retries with
    // an override + reason (backend requires the reason to be ≥ 10 chars).
    const body = opts?.override
      ? { override: true, override_reason: opts.overrideReason ?? '' }
      : {};
    await post(`${API}/payroll-runs/${runId}/lock`, body);
    return this.getRunDetail(runId);
  },

  async getControlCenter(runId): Promise<ControlCenter> {
    const d = await get<any>(`${API}/payroll-runs/${runId}/control-center`);
    return mapControlCenter(d);
  },

  async updateRiskAlert(alertId, status, reason): Promise<RiskAlert> {
    const a = await post<any>(`${API}/payroll/risk-alerts/${alertId}/status`, {
      status,
      ...(reason ? { reason } : {}),
    });
    return mapRiskAlert(a);
  },

  async requestExport(runId, type, format, force): Promise<PayrollExport> {
    const rec = await post<any>(`${API}/payroll-runs/${runId}/exports`, {
      export_type: type,
      format,
      // force mints a NEW immutable version even when the run is unchanged
      // (without it, an unchanged run reuses the existing artifact).
      ...(force ? { options: { force: true } } : {}),
    });
    return mapExport(rec);
  },

  async getSubmissionDestinations(runId): Promise<SubmissionDestination[]> {
    const res = await get<any>(`${API}/payroll-runs/${runId}/submission-destinations`);
    return (res.destinations ?? []).map((d: any) => ({
      exportType: d.export_type,
      target: d.target,
      filingType: d.filing_type,
      bankCode: d.bank_code || undefined,
      configured: !!d.configured,
      templateName: d.template_name || undefined,
      templateVersion: d.template_version || undefined,
      defaultFormat: d.default_format || undefined,
      deliveryMethod: d.delivery_method,
    }));
  },

  async prepareBatch(runId, _preparedBy): Promise<DisbursementBatch | null> {
    const b = await post<any>(`${API}/payroll-runs/${runId}/disbursements`);
    return mapBatch(b);
  },

  async submitBatch(runId, _submittedBy, opts): Promise<DisbursementBatch | null> {
    const list = await get<{ batches: any[] }>(`${API}/payroll-runs/${runId}/disbursements`);
    const first = (list.batches ?? [])[0];
    if (!first) return null;
    // Body only when overriding, so an ordinary release is byte-identical to what
    // it has always sent.
    const body = opts?.override
      ? { override: true, override_reason: opts.overrideReason ?? '' }
      : undefined;
    const b = await post<any>(`${API}/disbursement-batches/${first.id}/submit`, body);
    return mapBatch(b);
  },

  async cancelBatch(batchId): Promise<DisbursementBatch> {
    return mapBatch(await post<any>(`${API}/disbursement-batches/${batchId}/cancel`));
  },

  async releaseBatch(batchId): Promise<DisbursementBatch> {
    return mapBatch(await post<any>(`${API}/disbursement-batches/${batchId}/submit`));
  },

  async retryDisbursement(itemId): Promise<DisbursementItem> {
    return mapItem(await post<any>(`${API}/disbursements/${itemId}/retry`));
  },

  async getReconciliationQueue(): Promise<DisbursementItem[]> {
    const d = await get<{ items: any[]; count: number }>(`${API}/disbursements/reconciliation`);
    return (d.items ?? []).map(mapItem);
  },

  async getRecentBatches(limit = 25): Promise<DisbursementBatch[]> {
    const d = await get<{ batches: any[] }>(`${API}/disbursement-batches?limit=${limit}`);
    return (d.batches ?? []).map(mapBatch);
  },

  async resolveDisbursement(itemId, input): Promise<DisbursementItem> {
    return mapItem(await post<any>(`${API}/disbursements/${itemId}/resolve`, input));
  },

  async disburseSettlement(settlementId): Promise<DisbursementBatch> {
    return mapBatch(await post<any>(`${API}/payroll/terminal-dues/settlements/${settlementId}/disburse`));
  },

  async listPayslips(): Promise<Payslip[]> {
    const data = await get<{ payslips: any[] }>(`${API}/payslips`);
    return (data.payslips ?? []).map((p) => ({
      id: p.id,
      employeeName: p.employee_name ?? '',
      periodLabel: humanizePeriod(p.period_label),
      payDate: p.generated_at ?? '',
      country: 'TZ' as CountryCode,
      currency: (p.currency || 'TZS') as Currency,
      gross: num(p.gross),
      net: num(p.net),
      status: p.status === 'generated' ? 'generated' : 'pending',
      components: [],
    }));
  },

  async payslipDownloadUrl(id: string): Promise<string | null> {
    const d = await get<{ download_url?: string }>(`${API}/payslips/${id}`);
    return d.download_url ?? null;
  },

  async exportDownloadUrl(id: string): Promise<string | null> {
    const d = await get<{ download_url?: string }>(`${API}/exports/${id}/download`);
    return d.download_url ?? null;
  },

  // Excel preview: streamed through the authenticated API (not a presigned URL,
  // since previews are never stored). Fetch the bytes then trigger a client-side
  // download. This never touches the official file or its checksum.
  async previewExport(id: string): Promise<void> {
    const res = await http.get(`${API}/exports/${id}/preview`, { responseType: 'blob' });
    const cd = (res.headers?.['content-disposition'] as string | undefined) ?? '';
    const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? `preview_${id}.xlsx`;
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // --- Statutory filing register (gateway Phase 4) -------------------------

  async getFilingRegister(year?: number, month?: number): Promise<FilingRegister> {
    const qs = year != null && month != null ? `?year=${year}&month=${month}` : '';
    const [d, le] = await Promise.all([
      get<any>(`${API}/filings${qs}`),
      legalEntities(),
    ]);
    return mapFilingRegister(d, le);
  },

  async generateFiling(input: NewFilingInput): Promise<Filing> {
    const [f, le] = await Promise.all([
      post<any>(`${API}/filings`, {
        legal_entity_id: input.legalEntityId,
        filing_type: input.filingType,
        year: input.year,
        month: input.month,
      }),
      legalEntities(),
    ]);
    return mapFiling(f, le);
  },

  async getFiling(id: string): Promise<Filing> {
    const [f, le] = await Promise.all([get<any>(`${API}/filings/${id}`), legalEntities()]);
    return mapFiling(f, le);
  },

  async approveFiling(id: string): Promise<Filing> {
    return mapFiling(await post<any>(`${API}/filings/${id}/approve`), await legalEntities());
  },

  async submitFiling(id: string, ackRef?: string): Promise<Filing> {
    const body = ackRef?.trim() ? { acknowledgment_ref: ackRef.trim() } : {};
    return mapFiling(await post<any>(`${API}/filings/${id}/submit`, body), await legalEntities());
  },

  async recordFilingOutcome(id: string, input: FilingOutcomeInput): Promise<Filing> {
    const body: any = { result: input.result };
    if (input.acknowledgmentRef?.trim()) body.acknowledgment_ref = input.acknowledgmentRef.trim();
    if (input.controlNumber?.trim()) body.control_number = input.controlNumber.trim();
    if (input.rejectionReason?.trim()) body.rejection_reason = input.rejectionReason.trim();
    return mapFiling(await post<any>(`${API}/filings/${id}/outcome`, body), await legalEntities());
  },

  async recordFilingPayment(id: string, ref: string): Promise<Filing> {
    return mapFiling(
      await post<any>(`${API}/filings/${id}/payment`, { payment_reference: ref }),
      await legalEntities(),
    );
  },

  async amendFiling(id: string): Promise<Filing> {
    // Returns the NEW filing (version+1); the old one flips to `amended`.
    return mapFiling(await post<any>(`${API}/filings/${id}/amend`), await legalEntities());
  },

  async uploadFilingEvidence(id: string, file: File, note?: string): Promise<FilingEvidence> {
    const form = new FormData();
    form.append('file', file);
    if (note?.trim()) form.append('note', note.trim());
    // Let the browser set the multipart boundary; the auth interceptor still applies.
    const res = await http.post<any>(`${API}/filings/${id}/evidence`, form);
    return mapEvidence(res.data);
  },

  async filingEvidenceUrl(id: string, evidenceId: string): Promise<string | null> {
    // Same auth pattern as exportDownloadUrl: an authenticated GET through the
    // gateway client. The endpoint redirects to a presigned URL — axios follows
    // it, so the final responseURL (or an explicit download_url body) is the
    // presigned link the caller can window.open without auth headers.
    const res = await http.get<any>(`${API}/filings/${id}/evidence/${evidenceId}/download`);
    if (res.data && typeof res.data === 'object' && typeof res.data.download_url === 'string') {
      return res.data.download_url;
    }
    const finalUrl: string | undefined = (res.request as XMLHttpRequest | undefined)?.responseURL;
    return finalUrl && !finalUrl.includes(`/filings/${id}/evidence/`) ? finalUrl : null;
  },

  async listLegalEntities(): Promise<LegalEntityLite[]> {
    const le = await legalEntities(true); // always fresh — the picker must reflect current entities
    return Array.from(le.values()).map((e) => ({
      id: e.id,
      name: e.name,
      country: e.country,
      currency: e.base_currency ?? 'TZS',
    }));
  },

  async listEmployees(): Promise<EmployeeLite[]> {
    const data = await get<{ employees: any[] }>(`${API}/payroll/employees`).catch(() => ({ employees: [] }));
    return (data.employees ?? []).map((e) => ({ id: e.employee_id, name: e.full_name }));
  },

  async createRun(input): Promise<CreateRunResult> {
    const le = await legalEntities();
    // The status is the answer, not just the body: 201 = a new run, 200 = the
    // period already had one and this IS that run (uq_run_period). Reading only
    // the body is how an existing, validated run got announced as "created".
    const res = await http.post<RunView>(`${API}/payroll-runs`, {
      legal_entity_id: input.legalEntityId,
      period_start: input.periodStart,
      period_end: input.periodEnd,
      label: input.label?.trim() || undefined,
    });
    leCache = null; // refresh enrichment next read
    return { run: mapRun(res.data, le), created: res.status === 201 };
  },
};

function humanizePeriod(label: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(label || '');
  if (!m) return label || '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// --- statutory filing mapping -----------------------------------------------

function mapFilingIssue(v: any): { level: 'error' | 'warning'; code: string; message: string } {
  return {
    level: v?.level === 'error' ? 'error' : 'warning',
    code: v?.code ?? '',
    message: v?.message ?? '',
  };
}

function mapEvidence(e: any): FilingEvidence {
  return {
    id: e?.id ?? '',
    fileName: e?.file_name ?? '',
    contentType: e?.content_type ?? '',
    sizeBytes: Number(e?.size_bytes ?? 0),
    note: e?.note || undefined,
    uploadedBy: e?.uploaded_by || undefined,
    uploadedAt: e?.uploaded_at ?? '',
  };
}

/** amount_due → FilingAmount, or null when absent/malformed (never a 0). */
export function mapFilingAmount(v: any): FilingAmount | null {
  if (!v || typeof v !== 'object') return null;
  const amount = typeof v.amount === 'string' ? v.amount : v.amount != null ? String(v.amount) : '';
  if (amount === '' || !Number.isFinite(Number(amount))) return null;
  return { amount, currency: typeof v.currency === 'string' && v.currency ? v.currency : '' };
}

function mapAmountFields(v: any): { amountDue: FilingAmount | null; amountStatus: FilingAmountStatus } {
  const amountDue = v?.amount_status === 'calculated' ? mapFilingAmount(v?.amount_due) : null;
  return { amountDue, amountStatus: amountDue ? 'calculated' : 'not_calculated' };
}

export function mapFilingRegister(d: any, le?: Map<string, LegalEntity>): FilingRegister {
  const c = d?.counts;
  const counts =
    c && typeof c === 'object'
      ? { outstanding: Number(c.outstanding ?? 0), overdue: Number(c.overdue ?? 0), completed: Number(c.completed ?? 0) }
      : null;
  return {
    period: d?.period ?? '',
    filings: Array.isArray(d?.filings) ? d.filings.map((f: any) => mapFiling(f, le)) : [],
    outstanding: Array.isArray(d?.outstanding) ? d.outstanding.map(mapOutstanding) : [],
    counts,
    totalsDue: Array.isArray(d?.totals_due)
      ? (d.totals_due.map(mapFilingAmount).filter(Boolean) as FilingAmount[])
      : [],
  };
}

function mapFiling(f: any, le?: Map<string, LegalEntity>): Filing {
  return {
    id: f.id,
    legalEntityId: f.legal_entity_id,
    legalEntityName: f.legal_entity_name || le?.get(f.legal_entity_id)?.name || '—',
    filingType: f.filing_type as FilingType,
    period: f.period ?? '',
    periodYear: f.period_year ?? 0,
    periodMonth: f.period_month ?? 0,
    version: f.version ?? 1,
    status: (f.status as FilingStatus) ?? 'generated',
    runId: f.run_id ?? '',
    exportId: f.export_id ?? '',
    validation: Array.isArray(f.validation) ? f.validation.map(mapFilingIssue) : [],
    dueDate: f.due_date || undefined,
    overdue: !!f.overdue,
    approvedBy: f.approved_by || undefined,
    approvedAt: f.approved_at || undefined,
    submittedBy: f.submitted_by || undefined,
    submittedAt: f.submitted_at || undefined,
    acknowledgmentRef: f.acknowledgment_ref || undefined,
    controlNumber: f.control_number || undefined,
    paymentReference: f.payment_reference || undefined,
    rejectionReason: f.rejection_reason || undefined,
    supersededBy: f.superseded_by || undefined,
    notes: f.notes || undefined,
    createdAt: f.created_at ?? '',
    updatedAt: f.updated_at ?? '',
    evidence: Array.isArray(f.evidence) ? f.evidence.map(mapEvidence) : undefined,
    ...mapAmountFields(f),
  };
}

function mapOutstanding(o: any): OutstandingFiling {
  return {
    legalEntityId: o?.legal_entity_id ?? '',
    legalEntityName: o?.legal_entity_name ?? '—',
    filingType: o?.filing_type as FilingType,
    period: o?.period ?? '',
    dueDate: o?.due_date || undefined,
    overdue: !!o?.overdue,
    ...mapAmountFields(o),
  };
}

// --- control-center mapping -------------------------------------------------

function mapRiskAlert(a: any): RiskAlert {
  return {
    id: a.id,
    employeeId: a.employee_id || undefined,
    ruleCode: a.rule_code ?? '',
    severity: (a.severity as AlertSeverity) ?? 'low',
    category: (a.category as AlertCategory) ?? 'payroll',
    message: a.message ?? '',
    recommendedAction: a.recommended_action || undefined,
    status: (a.status as AlertStatus) ?? 'open',
    overrideReason: a.override_reason || undefined,
    actionLink: a.action_link || undefined,
    createdAt: a.created_at ?? '',
  };
}

function mapControlCenter(d: any): ControlCenter {
  // Statutory totals arrive as decimal strings with only the keys the country
  // pack produces — keep the same "only present keys" semantics in the UI type.
  const st = d?.statutory_totals ?? {};
  const statutoryTotals: ControlCenter['statutoryTotals'] = {};
  if (st.paye != null) statutoryTotals.paye = num(st.paye);
  if (st.nssf_employee != null) statutoryTotals.nssfEmployee = num(st.nssf_employee);
  if (st.nssf_employer != null) statutoryTotals.nssfEmployer = num(st.nssf_employer);
  if (st.sdl != null) statutoryTotals.sdl = num(st.sdl);
  if (st.wcf != null) statutoryTotals.wcf = num(st.wcf);
  if (st.heslb != null) statutoryTotals.heslb = num(st.heslb);

  const pop = d?.population ?? {};
  const changes = d?.changes ?? {};
  const loans = d?.loans ?? {};
  const readiness = d?.readiness ?? {};
  const att = d?.attendance;

  return {
    runId: d?.run?.id ?? '',
    state: (d?.run?.state as RunState) ?? 'draft',
    currency: (d?.run?.currency as Currency) ?? 'TZS',
    population: {
      total: pop.total ?? 0,
      calculated: pop.calculated ?? 0,
      errors: pop.errors ?? 0,
      excluded: pop.excluded ?? 0,
    },
    totals: d?.totals
      ? { gross: num(d.totals.gross), net: num(d.totals.net), employerCost: num(d.totals.employer_cost) }
      : undefined,
    statutoryTotals,
    changes: { salaryChanges: changes.salary_changes ?? 0, newHires: changes.new_hires ?? 0 },
    attendance: att
      ? {
          hasData: !!att.has_data,
          unapprovedOvertime: att.unapproved_overtime ?? 0,
          openExceptions: att.open_exceptions ?? 0,
          openCriticalExceptions: att.open_critical_exceptions ?? 0,
          paidWithZeroAttendance: att.paid_with_zero_attendance ?? 0,
        }
      : undefined,
    loans: { activeCount: loans.active_count ?? 0, expectedDeductions: num(loans.expected_deductions) },
    readiness: {
      total: readiness.total ?? 0,
      ready: readiness.ready ?? 0,
      warning: readiness.warning ?? 0,
      blocked: readiness.blocked ?? 0,
      excluded: readiness.excluded ?? 0,
    },
    alerts: Array.isArray(d?.alerts) ? d.alerts.map(mapRiskAlert) : [],
    riskScore: Math.max(0, Math.min(100, Number(d?.risk_score ?? 0))),
    gate: {
      status: (d?.gate?.status as GateStatus) ?? 'ready',
      blocking: Array.isArray(d?.gate?.blocking) ? d.gate.blocking : [],
    },
    taxTreatment: mapTaxTreatment(d?.tax_treatment),
  };
}

/** ME7: the run review's "Confirm tax treatment" section. A malformed or
 *  unknown status is shown as unavailable — never as "nobody to confirm". */
export function mapTaxTreatment(tt: any): ControlCenter['taxTreatment'] {
  if (tt == null) return undefined;
  const findings = Array.isArray(tt.findings)
    ? tt.findings.map((f: any) => ({
        personId: String(f.person_id ?? ''),
        fullName: f.full_name || undefined,
        legalEntityId: String(f.legal_entity_id ?? ''),
        employmentEngagementId: String(f.employee_engagement_id ?? ''),
        contractorEngagementId: String(f.contractor_engagement_id ?? ''),
        generation: String(f.generation ?? ''),
        provisional: !!f.provisional,
        overlapFrom: f.overlap_from ? String(f.overlap_from).slice(0, 10) : undefined,
        overlapTo: f.overlap_to ? String(f.overlap_to).slice(0, 10) : undefined,
      }))
    : [];
  return {
    status: tt.status === 'ok' && Array.isArray(tt.findings) ? 'ok' : 'unavailable',
    findings,
    errors: Array.isArray(tt.errors) ? tt.errors.map(String) : [],
  };
}

// --- export + disbursement mapping ----------------------------------------

function mapExport(e: any): PayrollExport {
  return {
    id: e.id,
    runId: e.payroll_run_id,
    type: e.export_type as ExportType,
    format: e.format as ExportFormat,
    status: e.status as ExportStatus,
    version: e.version ?? 1,
    fileName: e.file_name || undefined,
    checksum: e.checksum || undefined,
    generatedAt: e.generated_at || undefined,
    category: e.category || undefined,
    officialFormat: e.official_format || undefined,
    submissionTarget: e.submission_target || undefined,
    checksumAlgorithm: e.checksum_algorithm || undefined,
    generatedBy: e.generated_by || undefined,
    totals: e.totals || undefined,
    metadata: e.metadata || undefined,
    errorCount: e.error_count ?? undefined,
    errors: Array.isArray(e.errors)
      ? e.errors.map((x: any) => ({ severity: x.severity, code: x.code, field: x.field, message: x.message, employeeId: x.employee_id, employeeName: x.employee_name }))
      : undefined,
    invalidatedAt: e.invalidated_at || undefined,
    invalidatedReason: e.invalidated_reason || undefined,
    templateVersion: e.template_version || undefined,
    bankCode: e.bank_code || undefined,
  };
}

async function loadBatch(batchId: string, _currency: Currency): Promise<DisbursementBatch | null> {
  const b = await get<any>(`${API}/disbursement-batches/${batchId}`).catch(() => null);
  return b ? mapBatch(b) : null;
}

function mapItem(it: any): DisbursementItem {
  return {
    id: it.id,
    employeeName: it.beneficiary_name ?? '',
    channel: (it.channel as DisbursementChannel) ?? 'bank',
    provider: it.provider ?? '',
    accountMasked: it.account_or_wallet ?? '',
    amount: num(it.amount),
    currency: (it.currency as Currency) || undefined,
    status: (it.status as DisbursementItemStatus) ?? 'pending',
    allocationSeq: it.allocation_seq ? Number(it.allocation_seq) : undefined,
    connectorRef: it.connector_ref || undefined,
    attempts: it.attempts ?? 0,
    submittedAt: it.submitted_at || undefined,
    retryClass: (it.retry_class as RetryClass) || undefined,
    reconciliationNote: it.reconciliation_note || undefined,
    reconciledBy: it.reconciled_by ?? undefined,
    reconciledAt: it.reconciled_at ?? undefined,
  };
}

function mapBatch(b: any): DisbursementBatch {
  return {
    id: b.id,
    runId: b.payroll_run_id,
    connector: b.connector,
    status: b.status as DisbursementBatchStatus,
    purpose: (b.purpose as DisbursementPurpose) || undefined,
    settlementId: b.settlement_id || undefined,
    loanId: b.loan_id || undefined,
    currency: b.currency as Currency,
    totalAmount: num(b.total_amount),
    itemCount: b.item_count ?? (b.items?.length ?? 0),
    statusCounts: b.status_counts ?? {},
    paidTotal: num(b.paid_total),
    fullyReconciled: !!b.fully_reconciled,
    preparedBy: b.prepared_by || undefined,
    submittedBy: b.submitted_by || undefined,
    preparedAt: b.prepared_at ?? '',
    submittedAt: b.submitted_at || undefined,
    completedAt: b.completed_at || undefined,
    items: (b.items ?? []).map(mapItem),
  };
}
